import React, { FC, Suspense, useCallback, useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay/hooks';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AIStudioDialog } from 'src/components/pages/aiStudio/aiStudioDialog';
import ProductCatalogDialog from 'src/components/pages/aiStudio/components/dialogs/productCatalogDialog';
import { ProductSideImage } from 'src/components/pages/aiStudio/types';
import { ProductColor } from 'src/components/pages/productConfiguration/state/model/modelColor';
import ConnectModelDialog from 'src/components/pages/productConfiguration/steps/6-done/connectModelDialog';
import DuplicateDialog from 'src/components/pages/productConfiguration/steps/6-done/duplicateDialog';
import { productListConnectModelMutation$data } from 'src/components/pages/products/__generated__/productListConnectModelMutation.graphql';
import MarketplacePreviewUrlDialog from 'src/components/pages/products/marketplacePreviewUrlDialog';
import { DialogOverlayConnect } from 'src/components/pages/products/productList';
import ProductSidePanel from 'src/components/pages/products/productSidebar';
import { TestPageSkeleton } from 'src/components/pages/testPage/testPageSkeleton';
import AddSalesChannelDialog from 'src/components/pages/salesChannels/addSalesChannelDialog';
import { SalesChannelNotAvailableDialogWindow } from 'src/components/pages/salesChannels/salesChannel';
import ChangeProductIcon from 'src/components/pages/userInterfaceSettings/icons/change-product.svg';
import DesktopWideIcon from 'src/components/pages/userInterfaceSettings/icons/desktop-wide.svg';
import DesktopIcon from 'src/components/pages/userInterfaceSettings/icons/desktop.svg';
import MobileIcon from 'src/components/pages/userInterfaceSettings/icons/mobile.svg';
import type { LivePreviewSize } from 'src/components/pages/userInterfaceSettings/livePreview/livePreview';
import mobileIphone380x844Skin from 'src/components/pages/userInterfaceSettings/livePreview/skins/mobile-iphone380x844.png';
import { Badge } from 'src/components/widgets/badge';
import {
    FullscreenIframe,
    FullscreenIframeCloseButton,
    FullscreenIframeContent,
    FullscreenIframeFrameContainer,
    FullscreenIframeOverlay,
    FullscreenIframeTitle,
    FullscreenIframeTopBar,
    FullscreenIframeTopBarCenter,
    FullscreenIframeTopBarLeft,
    FullscreenIframeTopBarRight
} from 'src/components/widgets/fullscreenIframeLayout';
import { useRelayStore } from 'src/relay';
import config from 'src/shared/config';
import { NoIntegrationDialog, useUpgradeDialogs } from 'src/shared/dialogs';
import { useFeatureFlag } from 'src/shared/feature-flags';
import { Center } from 'src/shared/globals';
import { backofficeUrl, stringFormat, T, toLocalID, uuid } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useAutoAddonActivation } from 'src/shared/hooks/useAutoAddonActivation';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import ProductDetailsIcon from 'src/shared/material_icons/action/info.svg';
import ExternalLinkIcon from 'src/shared/material_icons/action/open_in_new.svg';
import CloseIcon from 'src/shared/material_icons/navigation/close.svg';
import VerticalThreePoint from 'src/shared/material_icons/navigation/more_vert.svg';
import {
    isComposerAddToCartMessage,
    isCustomizerAddToCartMessage,
    isDiscoveryAiGenerationCompletedMessage,
    isDiscoveryModeHiddenToolsMessage,
    isDiscoveryModeInteractiveMessage,
    isDiscoveryModeReadyMessage,
    isTestModeReadyMessage,
    postAddonsList,
    type ZakekeMessageDiscoveryModeUpdate
} from 'src/shared/zakekeMessage';
import styled, { css } from 'styled-components';
import { Link } from '../buttons';
import { MessageDialog, QuestionDialog, useDialogManager } from '../dialog';
import { DropdownActionItem, DropdownActions, DropdownDivider } from '../dropdown';
import { Icon } from '../icon';
import Loader from '../loader';
import { NotificationBanner } from '../notificationBanner';
import {
    removeNotification,
    showErrorNotification,
    showInfoNotification,
    showNotification,
    showSuccessNotification,
    showWarningNotification
} from '../notificationToaster';
import {
    getShopifyDuplicationWarningText,
    getShopifyDuplicationWarningTitle,
    SHOPIFY_DUPLICATION_WARNING_LEARN_MORE_URL,
    SHOPIFY_INTEGRATION_SETTINGS_PATH
} from '../shopifyDuplicationWarning/shopifyDuplicationWarning';
import UpgradeDialog from '../upgradeDialog/upgradeDialog';
import WaitForProductExists from '../waitForProductExists';
import { ZSwitch } from '../zswitch';
import { ZTooltip } from '../ztooltip';
import { testPageConnectModelMutation, testPageConnectModelMutation$data } from './__generated__/testPageConnectModelMutation.graphql';
import { testPageDeleteMutation } from './__generated__/testPageDeleteMutation.graphql';
import { testPageDuplicateMutation, testPageDuplicateMutation$data } from './__generated__/testPageDuplicateMutation.graphql';
import { testPageOrderMutation } from './__generated__/testPageOrderMutation.graphql';
import { testPageProductSetStatusMutation } from './__generated__/testPageProductSetStatusMutation.graphql';
import { testPageQuery } from './__generated__/testPageQuery.graphql';
import { DISCOVERY_AI_PAYLOADS } from './discoveryAiTools';
import { DiscoveryCollagePrefetch } from './discoveryCollagePrefetch';
import { DiscoveryAiCreditCase, useDiscoveryAiCredits } from './useDiscoveryAiCredits';
import DiscoveryAiActivationBanner from './discoveryAiActivationBanner';
import DiscoveryAiCreditsPurchaseDialog from './discoveryAiCreditsPurchaseDialog';
import { DiscoveryModePanel } from './DiscoveryModePanel';
import { DISCOVERY_PRICING_PAYLOADS, QUANTITY_TIERS_PRICING_RULE } from './discoveryPricingRules';
import { DISCOVERY_PRINTING_PAYLOADS } from './discoveryPrintMethods';
import { DISCOVERY_TOOLS_PAYLOADS } from './discoveryTools';
import TestOrdersPurchaseDialog from './testOrdersPurchaseDialog';

// Quantity presets for the discovery quantity-tiers control, derived from the tier breakpoints so the
// buttons always line up with where the per-unit price changes.
const DISCOVERY_QUANTITY_PRESETS = QUANTITY_TIERS_PRICING_RULE.tiers.map((tier) => tier.minQuantity);

const ChangeProductButton = styled.button<{ $discovery?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 9999px;
    background: ${(props) => (props.$discovery ? 'rgba(255, 255, 255, 0.15)' : '#f3f5f6')};
    color: ${(props) => (props.$discovery ? '#ffffff' : '#000000')};
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease;

    &:hover {
        background: ${(props) => (props.$discovery ? 'rgba(255, 255, 255, 0.25)' : '#f97316')};
        color: white;
    }

    ${Icon} {
        width: 22px;
        height: 22px;
        fill: currentColor;
        min-width: 0;
        min-height: 0;
    }
`;

const StyledBadge = styled(Badge)`
    margin-left: 0;
`;

const BadgeContainer = styled.div`
    min-width: 110px;
`;

const BannerSection = styled.div`
    flex-shrink: 0;
    padding: 12px 24px 0;
`;

const PublishSwitchWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: -20px;
`;

const PublishSwitchLabel = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: #1e293b;
`;

const TestOrderNumberInBillingDateer = styled.div<{ $warning?: boolean }>`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
    background: ${(props) => (props.$warning ? '#fef2f2' : '#f1f5f9')};
    color: ${(props) => (props.$warning ? '#dc2626' : '#475569')};
    border: 1px solid ${(props) => (props.$warning ? '#fecaca' : '#e2e8f0')};
`;

const ModeToggleContainer = styled.div`
    display: inline-flex;
    flex-shrink: 0;
    background: #e2e8f0;
    border-radius: 8px;
    padding: 3px;
    gap: 2px;
`;

const ModeToggleButton = styled.button<{ $active: boolean; $variant?: 'test' | 'discovery' }>`
    min-width: 150px;
    padding: 6px 20px;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
    text-align: center;

    background: ${(props) => {
        if (!props.$active) return 'transparent';
        return props.$variant === 'discovery' ? 'linear-gradient(135deg, #E8650A 0%, #E6167F 100%)' : 'white';
    }};
    color: ${(props) => {
        if (!props.$active) return '#64748b';
        return props.$variant === 'discovery' ? '#ffffff' : '#1e293b';
    }};
    box-shadow: ${(props) => (props.$active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none')};

    &:hover {
        color: ${(props) => {
            if (!props.$active) return '#334155';
            return props.$variant === 'discovery' ? '#ffffff' : '#1e293b';
        }};
    }
`;

const UISettingsButton = styled.button`
    padding: 6px 14px;
    border: 1.5px solid rgba(255, 255, 255, 0.4);
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:hover {
        background: rgba(255, 255, 255, 0.25);
        border-color: rgba(255, 255, 255, 0.6);
    }
`;

type SkinOptions = {
    url: string;
    gutterTop: number;
    gutterBottom: number;
    gutterLeft: number;
    gutterRight: number;
};

const previewSizeConfigs: Record<LivePreviewSize, { width: number; height: number; skin: SkinOptions | null }> = {
    'desktop-wide': {
        width: window.innerWidth,
        height: window.innerHeight,
        skin: null
    },
    desktop: {
        width: 1220,
        height: 674,
        skin: null
    },
    mobile: {
        width: 380,
        height: 844,
        skin: {
            url: mobileIphone380x844Skin,
            gutterTop: 112,
            gutterBottom: 84,
            gutterLeft: 16,
            gutterRight: 16
        }
    }
};

const PreviewSkin = styled.div<{
    $width: number;
    $height: number;
    $scale: number;
    $skin?: SkinOptions | null;
}>`
    margin: auto;
    background: transparent;
    width: ${(props) => props.$width}px;
    height: ${(props) => props.$height}px;
    transform: scale(${(props) => props.$scale});
    transform-origin: center center;
    position: absolute;
    overflow: hidden;

    ${(props) =>
        !props.$skin &&
        css`
            border-radius: 16px;
            box-shadow:
                rgb(0, 0, 0, 0.15) 0 4px 20px 0,
                rgb(0, 0, 0, 0.15) 0 0 3px 0;
        `}

    ${(props) =>
        props.$skin &&
        css`
            padding-top: ${props.$skin.gutterTop}px;
            padding-bottom: ${props.$skin.gutterBottom}px;
            padding-left: ${props.$skin.gutterLeft}px;
            padding-right: ${props.$skin.gutterRight}px;

            &:after {
                content: ' ';
                position: absolute;
                inset: 0;
                background: url('${props.$skin.url}');
                z-index: 1;
                pointer-events: none;
                user-select: none;
            }
        `}
`;

const RoundIconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: none;
    border-radius: 9999px;
    background: #f3f5f6;
    color: #000000;
    cursor: pointer;
    transition:
        background 0.2s ease,
        color 0.2s ease;

    &:hover {
        background: #f97316;
        color: white;
    }

    ${Icon} {
        width: 22px;
        height: 22px;
        fill: currentColor;
        min-width: 0;
        min-height: 0;
    }
`;

// The preview area mirrors the theme editor live preview: the content floats on the gray background
// with no white card, for every layout (desktop, laptop, mobile).
const PreviewFrameContainer = styled(FullscreenIframeFrameContainer)`
    && {
        background: transparent;
        box-shadow: none;
        border-radius: 0;
    }
`;

const usePreviewSkinConfig = (containerRef: React.RefObject<HTMLDivElement>, size: LivePreviewSize) => {
    const [config, setConfig] = useState({ width: 0, height: 0, scale: 1, skin: null as SkinOptions | null });
    const updateConfig = useCallback(
        (size: LivePreviewSize) => {
            if (!containerRef.current) return;
            const { width, height, skin } = previewSizeConfigs[size];
            const scale = Math.min(1, (containerRef.current.clientWidth - 12) / width, (containerRef.current.clientHeight - 12) / height);
            setConfig({ width, height, scale, skin });
        },
        [size]
    );
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => updateConfig(size));
        if (containerRef.current) resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect();
    }, [size]);
    useEffect(() => updateConfig(size), [size]);
    return config;
};

type PreviewModel = testPageQuery['response']['model'];

const setIframeSearchParam = (iframeUrl: string, key: string, value: string) => {
    try {
        const url = new URL(iframeUrl);
        url.searchParams.set(key, value);
        return url.toString();
    } catch {
        const [urlWithoutHash, hash] = iframeUrl.split('#', 2);
        const separator = urlWithoutHash.includes('?') ? '&' : '?';
        return `${urlWithoutHash}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}${hash ? `#${hash}` : ''}`;
    }
};

const removeIframeSearchParam = (iframeUrl: string, key: string) => {
    try {
        const url = new URL(iframeUrl);
        url.searchParams.delete(key);
        return url.toString();
    } catch {
        const [urlWithoutHash, hash] = iframeUrl.split('#', 2);
        const [base, query] = urlWithoutHash.split('?', 2);
        if (!query) {
            return iframeUrl;
        }
        const kept = query.split('&').filter((pair) => pair.length > 0 && pair.split('=')[0] !== key);
        return `${base}${kept.length > 0 ? `?${kept.join('&')}` : ''}${hash ? `#${hash}` : ''}`;
    }
};

const TestPage: FC<{ productId: string; showCopyUrl?: boolean; isClassic?: boolean }> = ({ productId, showCopyUrl, isClassic }) => {
    // History
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const isPrototypeMode = searchParams.get('isPrototype') === '1';
    const { showPlanUpgradeDialog } = useUpgradeDialogs();
    const { showDialog, closeDialog } = useDialogManager();
    const { isAllowed } = usePermissions();
    const [incrementStoreKey] = useRelayStore((state) => [state.incrementRelayStoreKey]);
    const { sellerMarketplace } = useSubscriptionData();
    const isDiscoveryModeEnabled = useFeatureFlag('discoveryMode');
    const [hasResumed, setHasResumed] = useState(false);
    const [activeProductId, setActiveProductId] = useState(productId);
    const [showSidebar, setShowSidebar] = useState(false);

    const [fetchKey, setFetchKey] = useState(0);
    const [isPending, startTransition] = useTransition();
    const [discoveryMode, setDiscoveryMode] = useState(!isPrototypeMode && searchParams.get('mode') === 'discovery');
    const [isInfoBannerVisible, setIsInfoBannerVisible] = useState(() => localStorage.getItem('testPageInfoBannerDismissed') !== 'true');
    const [isShopifyDuplicationWarningVisible, setIsShopifyDuplicationWarningVisible] = useState(
        () => localStorage.getItem('testPageShopifyDuplicationWarningDismissed') !== 'true'
    );
    const [previewSize, setPreviewSize] = useState<LivePreviewSize>('desktop-wide');
    const effectivePreviewSize: LivePreviewSize = isPrototypeMode || discoveryMode ? 'desktop-wide' : previewSize;
    // Bumped to force the customizer iframe to remount and return to its initial state (e.g. after the
    // seller dismisses the "buy test orders" modal, which leaves the iframe stuck in its placing-order state).
    const [iframeReloadNonce, setIframeReloadNonce] = useState(0);
    const reloadIframe = useCallback(() => setIframeReloadNonce((nonce) => nonce + 1), []);
    const refreshProductIframe = useCallback(() => {
        startTransition(() => {
            setFetchKey((key) => key + 1);
            setIframeReloadNonce((nonce) => nonce + 1);
        });
    }, [startTransition]);
    const previewContainerRef = useRef<HTMLDivElement>(null);
    const skinConfig = usePreviewSkinConfig(previewContainerRef, effectivePreviewSize);
    // Per-category selections start empty on every load — they are intentionally NOT restored from the URL.
    // A page reload reboots the customizer iframe with no payload applied (the selection is streamed via
    // postMessage, not re-sent on the DiscoveryModeReady handshake), so restoring a previously-pressed button
    // would be incoherent: it would look active while the customizer is back in its clean discovery state.
    const [discoverySelections, setDiscoverySelections] = useState<Record<string, string | null>>(() => ({
        printing: null,
        pricing: null,
        ai: null,
        tools: null
    }));

    // Real Collage Layouts catalog (system grids), prefetched via DiscoveryCollagePrefetch while discovery mode is
    // active and streamed on the Collage Layouts click. A ref (not state) keeps handleDiscoverySelect's identity
    // stable and avoids a stale closure without re-creating the callback.
    const collagePayloadRef = useRef<object | null>(null);

    // --- Discovery AI credits (backoffice-owned gate) ---
    // The customizer has no GraphQL client, so the host owns the credit decision: when the seller engages an
    // AI Creative Tool we resolve the shared GENERATIVE_AI credit case and either open the real tool
    // (has-credits) or open a credit dialog first — activate the free trial (CASE 1) or buy a pack (CASE 3).
    const { resolve: resolveAiCredits, grantTrial } = useDiscoveryAiCredits();
    // The AI pill the seller engaged, held while a credit dialog is open so we can open the tool once credits
    // resolve to has-credits.
    const pendingAiToolRef = useRef<string | null>(null);
    // The resolved shared GENERATIVE_AI credit case, resolved when the seller presses an AI tool. Drives the
    // "billed via credits" activation banner (CASE 1) shown under the AI badges. Null until first resolved.
    const [aiCreditCase, setAiCreditCase] = useState<DiscoveryAiCreditCase | null>(null);
    const [isActivatingCredits, setIsActivatingCredits] = useState(false);

    // Streams an AI tool selection to the customizer (opens the real tool). Null clears the override.
    const streamAiSelection = useCallback((id: string | null) => {
        const message: ZakekeMessageDiscoveryModeUpdate = {
            zakekeMessageType: 'DiscoveryModeUpdate',
            category: 'ai',
            payload: id ? (DISCOVERY_AI_PAYLOADS[id] ?? null) : null
        };
        customizerIframeRef.current?.contentWindow?.postMessage(message, '*');
    }, []);

    // After a credit dialog grants/buys, tell the iframe to re-read its credit availability (so the just-
    // unlocked AI tools work without a reload) and then open the pending tool.
    const proceedWithPendingTool = useCallback(() => {
        customizerIframeRef.current?.contentWindow?.postMessage({ zakekeMessageType: 'DiscoveryModeRefreshCredits' }, '*');
        const tool = pendingAiToolRef.current;
        if (tool) streamAiSelection(tool);
    }, [streamAiSelection]);

    // CASE 3: open the buy-credits dialog; on a successful purchase, re-resolve and open the pending tool.
    const openPurchaseDialog = useCallback(() => {
        showDialog(
            'discovery-ai-purchase',
            <DiscoveryAiCreditsPurchaseDialog
                onPurchased={async () => {
                    const state = await resolveAiCredits();
                    if (state.creditCase === 'has-credits') proceedWithPendingTool();
                }}
            />
        );
    }, [showDialog, resolveAiCredits, proceedWithPendingTool]);

    // CASE 1: the seller has never activated. Grant the free trial (auto-refill off) from the persistent AI
    // panel banner; on success re-resolve, drop the banner, and open any tool the seller had already engaged.
    const handleActivateCredits = useCallback(async () => {
        setIsActivatingCredits(true);
        const granted = await grantTrial();
        if (!granted) {
            setIsActivatingCredits(false);
            showNotification({ type: 'danger', message: T._('Could not activate free credits. Please try again.', 'Admin'), dismiss: { duration: 3000 } });
            return;
        }
        const state = await resolveAiCredits();
        setAiCreditCase(state.creditCase);
        setIsActivatingCredits(false);
        if (state.creditCase === 'has-credits') proceedWithPendingTool();
    }, [grantTrial, resolveAiCredits, proceedWithPendingTool]);

    // Engage an AI tool: gate the real tool on the credit case resolved at this exact moment. CASE 2
    // (has-credits) streams straight through — the customizer shows its own "AI tools consume credits"
    // confirmation before running (useAICreditConfirmation), so the host doesn't duplicate it here.
    const engageAiTool = useCallback(
        async (id: string) => {
            pendingAiToolRef.current = id;
            const state = await resolveAiCredits();
            setAiCreditCase(state.creditCase);
            // CASE 1 no longer opens a modal: the persistent panel banner (shown while needs-activation) is the
            // activation path. We keep the engaged tool in pendingAiToolRef so activating from the banner opens it.
            if (state.creditCase === 'has-credits') streamAiSelection(id);
            else if (state.creditCase === 'out-of-credits') openPurchaseDialog();
        },
        [resolveAiCredits, streamAiSelection, openPurchaseDialog]
    );

    const handleDiscoverySelect = useCallback(
        (category: string, id: string | null) => {
            setDiscoverySelections((prev) => ({ ...prev, [category]: id }));

            // AI Creative Tools are gated on the seller's credit case at the moment of engagement: has-credits
            // streams straight through (real tool), otherwise a credit dialog opens first.
            if (category === 'ai') {
                if (id) void engageAiTool(id);
                else streamAiSelection(null);
                return;
            }

            // Non-AI selections stream immediately. Each wired button maps to a payload: printing → a raw
            // print-type, pricing → a sample pricing rule, tools → a tool marker. Deselecting (or a not-yet-
            // implemented button) resolves to null, which the customizer reads as "no override".
            let payload: object | null = null;
            if (id) {
                if (category === 'printing') payload = DISCOVERY_PRINTING_PAYLOADS[id] ?? null;
                else if (category === 'pricing') payload = DISCOVERY_PRICING_PAYLOADS[id] ?? null;
                else if (category === 'tools')
                    payload =
                        id === 'collage-layouts'
                            ? (collagePayloadRef.current ?? DISCOVERY_TOOLS_PAYLOADS[id] ?? null)
                            : (DISCOVERY_TOOLS_PAYLOADS[id] ?? null);
            }
            const message: ZakekeMessageDiscoveryModeUpdate = {
                zakekeMessageType: 'DiscoveryModeUpdate',
                category: category as ZakekeMessageDiscoveryModeUpdate['category'],
                payload
            };
            customizerIframeRef.current?.contentWindow?.postMessage(message, '*');
        },
        [engageAiTool, streamAiSelection]
    );

    // Simulated order quantity for the discovery sandbox (no footer quantity selector without a product
    // quantityRule). Streamed live to the customizer, which applies it via setQuantity so quantity-based
    // sample rules (quantity-tiers) re-tier without a reload.
    const [discoveryQuantity, setDiscoveryQuantity] = useState(1);

    const handleDiscoveryQuantityChange = useCallback((qty: number) => {
        setDiscoveryQuantity(qty);
        const message: ZakekeMessageDiscoveryModeUpdate = {
            zakekeMessageType: 'DiscoveryModeUpdate',
            category: 'quantity',
            payload: qty
        };
        customizerIframeRef.current?.contentWindow?.postMessage(message, '*');
    }, []);

    // Sync discovery state to URL search params so browser back restores it
    useEffect(() => {
        const params = new URLSearchParams(searchParams.toString());
        let changed = false;

        // mode
        if (discoveryMode) {
            if (params.get('mode') !== 'discovery') {
                params.set('mode', 'discovery');
                changed = true;
            }
        } else {
            if (params.has('mode')) {
                params.delete('mode');
                changed = true;
            }
        }

        // Per-category selections are intentionally NOT persisted to the URL (see the state init above): they
        // reset on reload to stay coherent with the freshly-booted iframe. Strip any stale selection params.
        for (const key of ['printing', 'pricing', 'ai', 'tools']) {
            if (params.has(key)) {
                params.delete(key);
                changed = true;
            }
        }

        if (changed) {
            const newSearch = params.toString();
            const newPath = `${location.pathname}${newSearch ? '?' + newSearch : ''}`;
            navigate(newPath, { replace: true });
        }
    }, [discoveryMode]);

    // Queries
    const {
        addons,
        model,
        overviewSubscription,
        testOrderNumberInBillingDate,
        subscription,
        salesChannels,
        shopifyAuthEcommerceSettings,
        sellerUserInterfaces
    } = useLazyLoadQuery<testPageQuery>(
        graphql`
            query testPageQuery($productId: ID!) {
                addons {
                    addonID
                    billingSystemAddonID
                    description
                    name
                    periodUnit
                    code
                    published
                    price
                    sellerID
                    rates {
                        feeValue
                    }
                }
                overviewSubscription {
                    chargeBeeSubscription {
                        currencyCode
                    }
                }
                model(id: $productId) {
                    id
                    name
                    code
                    provider
                    statusID
                    printTypes {
                        printTypeID
                    }
                    colors {
                        colorID
                        name
                        code
                        colorCode
                    }
                    iframeUrl {
                        testUrl
                        copyUrl
                    }
                    salesChannel {
                        id
                        ecommerceSettings {
                            ecommerceName
                            remoteUrl
                            productsApi
                            hasProductsApi
                            isMarketplace
                            definitiveUrl
                        }
                    }
                    zakekeEnvironment
                    canAddAdditionalCode
                    additionalCodes {
                        code
                        salesChannelID
                        merchantProductName
                        salesChannel {
                            id
                            ecommerceSettings {
                                ecommerceName
                                remoteUrl
                                definitiveUrl
                            }
                        }
                    }
                    modelColorMappings {
                        colorID
                        salesChannelID
                        merchantCode
                    }
                }
                testOrderNumberInBillingDate
                subscription {
                    maxProducts {
                        pricingVersion
                        numPublishedProducts
                        maxProducts
                        numProducts
                    }
                    maxNumTestOrders
                    isOverMaxNumTestOrders
                    inTrial
                }
                salesChannels(status: ACTIVE) {
                    id
                    ecommerceSettings {
                        ecommerceID
                        ecommerceName
                        isMarketplace
                        hasProductsApi
                        remoteUrl
                        definitiveUrl
                        currencyCode
                    }
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                }
                shopifyAuthEcommerceSettings {
                    isTestStore
                }
                sellerUserInterfaces(first: 1) {
                    nodes {
                        isDefault
                        userInterfaceId
                    }
                }
            }
        `,
        { productId: activeProductId },
        { fetchKey, fetchPolicy: 'network-only' }
    );

    if (!model) {
        throw new Error(`Oops: product not found!`);
    }

    const isPublished = model.statusID === 1;
    const addonsCurrency = overviewSubscription?.chargeBeeSubscription?.currencyCode ?? salesChannels.at(0)?.ecommerceSettings?.currencyCode ?? 'USD';
    const wasPublishedRef = useRef(isPublished);
    const [publishedInThisSession, setPublishedInThisSession] = useState(false);

    useEffect(() => {
        if (isPublished && !wasPublishedRef.current) {
            setPublishedInThisSession(true);
            setIsShopifyDuplicationWarningVisible(true);
            localStorage.removeItem('testPageShopifyDuplicationWarningDismissed');
        }

        wasPublishedRef.current = isPublished;
    }, [isPublished]);

    const isOldUserInterface = useMemo(() => {
        const defaultUI = sellerUserInterfaces?.nodes?.find((ui) => ui?.isDefault);
        return defaultUI?.userInterfaceId === 1;
    }, [sellerUserInterfaces]);

    useEffect(() => {
        if (hasResumed) return;

        const { doPublishImmediately, productID } = location.state || {};
        if (!doPublishImmediately || !productID || !model) return;

        handleSetStatusClick(model);

        setHasResumed(true);
    }, [hasResumed, location.state, model, navigate]);

    useEffect(() => {
        const shouldPublish = searchParams.get('publish') === 'true';

        if (shouldPublish) {
            handleSetStatusClick(model);
        }
    }, [searchParams, model]);

    // Mutations
    const [commitTestOrder] = useMutation<testPageOrderMutation>(graphql`
        mutation testPageOrderMutation($designDocID: String, $compositionDocID: String) {
            createTestOrder(designDocID: $designDocID, compositionDocID: $compositionDocID) {
                orderID
            }
        }
    `);

    const [setStatusProduct] = useMutation<testPageProductSetStatusMutation>(graphql`
        mutation testPageProductSetStatusMutation($input: SetStatusProductInput!) {
            setStatusProduct(input: $input) {
                model {
                    id
                    statusID
                }
                errors {
                    ... on Error {
                        message
                    }
                }
            }
        }
    `);

    const [commitDuplicate, isInFightDuplicate] = useMutation<testPageDuplicateMutation>(graphql`
        mutation testPageDuplicateMutation($id: ID!, $targets: [ModelDuplicateTargetInput!]!) {
            duplicateModel(id: $id, targets: $targets) {
                productIDs
                resultMessage
                status
            }
        }
    `);

    const [commitConnectModel, isInFightConnectModel] = useMutation<testPageConnectModelMutation>(graphql`
        mutation testPageConnectModelMutation($id: ID!, $target: ModelDuplicateTargetInput!) {
            connectModel(id: $id, target: $target)
        }
    `);

    const [deleteProduct, isInFightDelete] = useMutation<testPageDeleteMutation>(graphql`
        mutation testPageDeleteMutation($productID: String!) {
            deleteProduct(productID: $productID) {
                id
            }
        }
    `);

    // Hooks
    const openTestOrdersPurchaseDialog = useCallback(() => {
        // Return to this exact test product page after the (Shopify) charge is approved, stripping any
        // approval markers already present so they don't accumulate across attempts.
        const returnUrl = new URL(window.location.href);
        returnUrl.searchParams.delete('charge_id');
        returnUrl.searchParams.delete('forTEST_ORDERS');

        showDialog(
            'test-orders-purchase',
            <TestOrdersPurchaseDialog
                returnUrlAfterApproval={returnUrl.toString()}
                // Reload the iframe so it leaves the stuck placing-order state and returns to its initial state.
                onClose={reloadIframe}
                onPurchased={() => {
                    reloadIframe();
                    startTransition(() => setFetchKey((prev) => prev + 1));
                }}
            />
        );
    }, [showDialog, reloadIframe, startTransition, setFetchKey]);

    const onMessageReceivedFromIframe = useCallback(
        (event: MessageEvent) => {
            const data = event.data;
            const isAddToCart = isComposerAddToCartMessage(data) || isCustomizerAddToCartMessage(data);

            // No test orders left: prompt the seller to buy more instead of placing the order.
            if (isAddToCart && subscription?.isOverMaxNumTestOrders) {
                openTestOrdersPurchaseDialog();
                return;
            }

            // Composer add-to-cart: { zakekeMessageType: "AddToCart", message: { composition, preview, quantity } }
            if (isComposerAddToCartMessage(data)) {
                commitTestOrder({
                    variables: {
                        compositionDocID: data.message.composition
                    },
                    onCompleted: ({ createTestOrder }) => {
                        navigate(backofficeUrl(`/order/${createTestOrder?.orderID}`));
                    }
                });
                return;
            }

            // Customizer add-to-cart: { zakekeMessageType: 0, designId: "..." }
            if (isCustomizerAddToCartMessage(data)) {
                commitTestOrder({
                    variables: {
                        designDocID: data.designId
                    },
                    onCompleted: ({ createTestOrder }) => {
                        navigate(backofficeUrl(`/order/${createTestOrder?.orderID}`));
                    }
                });
            }
        },
        [commitTestOrder, isPrototypeMode, navigate, subscription?.isOverMaxNumTestOrders, openTestOrdersPurchaseDialog]
    );

    useEffect(() => {
        window.addEventListener('message', onMessageReceivedFromIframe);
        return () => window.removeEventListener('message', onMessageReceivedFromIframe);
    }, [onMessageReceivedFromIframe]);

    // Returning from the Shopify approval page for a test orders purchase: reopen the dialog so the
    // ServiceCredits flow detects the charge_id and finalizes the purchase on this same page.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('charge_id') && params.get('forTEST_ORDERS')) {
            openTestOrdersPurchaseDialog();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Listen for OPEN_AI_STUDIO postMessage from the customizer iframe
    useEffect(() => {
        const handleOpenAIStudio = (event: MessageEvent) => {
            if (event.data?.type === 'OPEN_AI_STUDIO') {
                const aiProductId = parseInt(event.data?.params?.productId, 10);
                const variantId = event.data?.params?.variantId ? parseInt(event.data?.params?.variantId, 10) : undefined;
                const docId = event.data?.params?.docId || undefined;
                const sidesData = event.data?.params?.sides;
                const model3DParam = event.data?.params?.model3D;
                const model3D = typeof model3DParam === 'string' || (typeof Blob !== 'undefined' && model3DParam instanceof Blob) ? model3DParam : undefined;
                const model3DRootUrl = typeof event.data?.params?.rootUrl === 'string' ? event.data.params.rootUrl : undefined;

                // Convert sides data to ProductSideImage array
                let sideImages: ProductSideImage[] | undefined;
                if (sidesData && sidesData.length > 0) {
                    sideImages = sidesData.map((side: { sideId: number; sideName: string; preview: string }) => ({
                        sideId: side.sideId,
                        sideName: side.sideName,
                        url: side.preview
                    }));
                }

                if (aiProductId) {
                    showDialog(
                        'ai-studio-dialog',
                        <AIStudioDialog
                            productId={aiProductId}
                            variantId={variantId}
                            sideImages={sideImages}
                            docId={docId}
                            model3D={model3D}
                            model3DRootUrl={model3DRootUrl}
                            onClose={() => closeDialog('ai-studio-dialog')}
                        />
                    );
                }
            }
        };

        window.addEventListener('message', handleOpenAIStudio);
        return () => window.removeEventListener('message', handleOpenAIStudio);
    }, [productId, showDialog, closeDialog]);

    const handleSetStatusClick = (product: PreviewModel) => {
        const pricingVersion = subscription?.maxProducts.pricingVersion ?? 'VERSION_1';
        if (pricingVersion === 'VERSION_2' && product!.statusID === 2) {
            const numCurrentPublishedProducts = subscription?.maxProducts.numPublishedProducts ?? 0;
            const numMaxPublishedProducts = subscription?.maxProducts.maxProducts ?? 0;

            if (!salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
                showDialog(
                    'complete-ecommerce-integration',
                    <NoIntegrationDialog
                        redirectPage={removeCultureAdmin()}
                        productId={toLocalID(productId) ?? 0}
                        subTitleNoIntegration={T._('Ready to start selling customizable products?', 'Admin')}
                        message={
                            <label>
                                {T._('You need to integrate Zakeke with your store in order to publish and start selling your customizable products.', 'Admin')}
                            </label>
                        }
                    ></NoIntegrationDialog>
                );
                return;
            } else if (numCurrentPublishedProducts >= numMaxPublishedProducts) {
                showDialog(
                    'Upgrade_Plan',
                    <UpgradeDialog
                        redirectUrlFromDashboard={encodeURI(window.location.href)}
                        marketPlace={sellerMarketplace ?? undefined}
                        message={
                            <>
                                {stringFormat(
                                    T._(
                                        `Your current plan allows for {0} published products, and you've reached your limit! To publish this product, upgrade your plan to increase your published product slots.`,
                                        'Admin'
                                    ),
                                    numMaxPublishedProducts.toString()
                                )}
                            </>
                        }
                        onUpgradeCompleted={async () => {
                            console.log('upgrade completato, continuo con la pubblicazione');
                            if (!showCopyUrl) {
                                showInfoNotification({
                                    message: product!.statusID === 1 ? T._('Unpublishing product', 'Admin') : T._('Publishing product', 'Admin')
                                });
                            }
                            setStatusProduct({
                                variables: {
                                    input: {
                                        productID: product!.id!,
                                        status: product!.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                                    }
                                },
                                onError: () => {
                                    showErrorNotification({
                                        message: product!.statusID === 1 ? T._('Failed Unpublish product', 'Admin') : T._('Failed Publish product', 'Admin')
                                    });
                                },
                                onCompleted: (response, errors) => {
                                    if (!showCopyUrl) {
                                        showSuccessNotification({
                                            message: product!.statusID === 1 ? T._('Product unpublished', 'Admin') : T._('Product published', 'Admin')
                                        });
                                    }
                                }
                            });
                        }}
                    />
                );

                return;
            }
        }

        if (
            product?.salesChannel.ecommerceSettings?.ecommerceName === 'NONE' &&
            salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE') &&
            product.additionalCodes.length == 0 &&
            product.modelColorMappings.length == 0 &&
            product?.statusID === 2
        ) {
            const realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');
            showDialog(
                'connectProductDialog',
                <ConnectModelDialog
                    productID={product.id}
                    additionalCodes={product.additionalCodes.map((c) => {
                        return { code: c.code, salesChannelID: c.salesChannelID, merchantProductName: c.merchantProductName };
                    })}
                    productColors={product.colors as ProductColor[]}
                    merchantProductOptionsConnected={[]}
                    salesChannels={realSalesChannels}
                    productSalesChannelID={product.salesChannel.id}
                    onConfirmMappingVariants={() => {
                        closeDialog('connectProductDialog');
                        if (!showCopyUrl) {
                            showInfoNotification({
                                message: product!.statusID === 1 ? T._('Unpublishing product', 'Admin') : T._('Publishing product', 'Admin')
                            });
                        }
                        setStatusProduct({
                            variables: {
                                input: {
                                    productID: product!.id!,
                                    status: product!.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                                }
                            },
                            onError: () => {
                                showErrorNotification({
                                    message: product!.statusID === 1 ? T._('Failed Unpublish product', 'Admin') : T._('Failed Publish product', 'Admin')
                                });
                            },
                            onCompleted: (response, errors) => {
                                if (!showCopyUrl) {
                                    showSuccessNotification({
                                        message: product!.statusID === 1 ? T._('Product unpublished', 'Admin') : T._('Product published', 'Admin')
                                    });
                                }
                            }
                        });
                    }}
                    onConfirm={async (sourceProduct, salesChannelId) => {
                        if (!product) {
                            showDialog('no-selection', <MessageDialog message={T._('Select a product', 'Admin')} />);
                            return;
                        }

                        if (!sourceProduct) return;

                        closeDialog('connectProductDialog');

                        const loadingNotification = showNotification({
                            type: 'default',
                            message: T._('Publishing your customizable product...', 'Admin')
                        });

                        let response!: testPageConnectModelMutation$data;

                        try {
                            // Save product
                            response = await new Promise((resolve, reject) =>
                                commitConnectModel({
                                    onCompleted: (response) => {
                                        resolve(response);
                                    },
                                    onError: (response) => reject(response),
                                    updater: (store) => {
                                        store.invalidateStore();
                                    },
                                    variables: {
                                        id: model!.id,
                                        target: {
                                            name: sourceProduct!.name,
                                            sku: sourceProduct!.sku,
                                            imageUrl: sourceProduct!.thumbnail,
                                            salesChannelId
                                        }
                                    }
                                })
                            );
                            showNotification({
                                type: 'success',
                                message: T._('Product connected and published!', 'Admin'),
                                dismiss: {
                                    duration: 3000
                                }
                            });

                            refreshAfterPublish();
                        } catch (ex: any) {
                            showNotification({
                                type: 'danger',
                                message: (
                                    <>
                                        {T._('Failed connecting and publishing product.', 'Admin')}
                                        <br />
                                        {ex.errors?.[0].extensions?.message}
                                    </>
                                ),
                                dismiss: {
                                    duration: 3000
                                }
                            });
                        }
                        removeNotification(loadingNotification);
                    }}
                    forPublishingProduct={true}
                />
            );

            return;
        } else if (!salesChannels.some((s) => s.ecommerceSettings?.ecommerceName !== 'NONE')) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    redirectPage={removeCultureAdmin()}
                    productId={toLocalID(productId) ?? 0}
                    subTitleNoIntegration={T._('Do you want to start selling your customizable products at your store?', 'Admin')}
                    message={
                        <label>{T._('Complete the integration with Zakeke by selecting your ecommerce platform and connecting your store.', 'Admin')}</label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        }

        if (!showCopyUrl) {
            showInfoNotification({ message: product!.statusID === 1 ? T._('Unpublishing product', 'Admin') : T._('Publishing product', 'Admin') });
        }
        setStatusProduct({
            variables: {
                input: {
                    productID: product!.id!,
                    status: product!.statusID === 1 ? 'UN_PUBLISHED' : 'PUBLISHED'
                }
            },
            onError: () => {
                showErrorNotification({ message: product!.statusID === 1 ? T._('Failed Unpublish product', 'Admin') : T._('Failed Publish product', 'Admin') });
            },
            onCompleted: (response, errors) => {
                if (!showCopyUrl) {
                    showSuccessNotification({ message: product!.statusID === 1 ? T._('Product unpublished', 'Admin') : T._('Product published', 'Admin') });
                }
            }
        });

        refreshAfterPublish();
    };

    const refreshAfterPublish = () => {
        const updatedSearchParams = new URLSearchParams(searchParams.toString());
        updatedSearchParams.delete('publish');

        const newSearch = updatedSearchParams.toString();
        const newPath = `${location.pathname}${newSearch ? '?' + newSearch : ''}`;

        startTransition(() => setFetchKey((prev) => prev + 1));
        navigate(newPath, { replace: true });
    };

    function removeCultureAdmin() {
        const { pathname, search } = window.location;
        const pattern = /^\/[^/]+\/Admin(?=\/|$)/i;

        const cleanedPath = pathname.replace(pattern, '/').replace(/\/+/g, '/');
        return `${cleanedPath}${search}`;
    }

    const handleAddSalesChannels = (modelID: string, salesChannelID: string) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        let realSalesChannels = salesChannels.filter((s) => s.ecommerceSettings?.ecommerceName !== 'NONE');

        // if (product.sourceProductTemplateID) {
        if (realSalesChannels.length == 0) {
            showDialog(
                'complete-ecommerce-integration',
                <NoIntegrationDialog
                    subTitleNoIntegration={T._('Ready to start selling customizable products?', 'Admin')}
                    message={
                        <label>
                            {T._('You need to integrate Zakeke with your store in order to publish and start selling your customizable products.', 'Admin')}
                        </label>
                    }
                ></NoIntegrationDialog>
            );
            return;
        } else if (realSalesChannels.length == 1) {
            shopifyAuthEcommerceSettings?.isTestStore
                ? showDialog(
                      'add-sales-channel-not-available',
                      <MessageDialog
                          windowDecorator={SalesChannelNotAvailableDialogWindow}
                          message={
                              <>
                                  {T._(
                                      "We're sorry, but you cannot add sales channels at this time. Your account is currently in trial mode on Shopify or is a development store.",
                                      'Admin'
                                  )}
                                  <br />
                                  <br />
                                  {T._('To access this feature, please upgrade to an active Shopify plan.', 'Admin')}
                                  <br />
                                  <br />
                                  {T._('Thank you for your understanding.', 'Admin')}
                              </>
                          }
                          buttons={[
                              {
                                  label: T._('OK', 'Admin'),
                                  onClick: () => {
                                      closeDialog('add-sales-channel-not-available');
                                  }
                              }
                          ]}
                      />
                  )
                : showDialog(
                      'add-sales-channel',
                      <AddSalesChannelDialog
                          onSalesChannelCreated={() => {
                              closeDialog('add-sales-channel');
                          }}
                      ></AddSalesChannelDialog>
                  );
        } else {
            let availableSalesChannels = realSalesChannels.filter(
                (rs) => rs.id != model.salesChannel.id && !model.additionalCodes.some((ac) => ac.salesChannelID == rs.id)
            );

            if (availableSalesChannels.length == 0) {
                showNotification({
                    type: 'danger',
                    message: T._('The product has been connected with all sales channels.', 'Admin'),
                    dismiss: {
                        duration: 3000
                    }
                });
                return;
            }

            showDialog(
                'connectProductDialog',
                <Suspense
                    fallback={
                        <DialogOverlayConnect>
                            <Loader />
                        </DialogOverlayConnect>
                    }
                >
                    <ConnectModelDialog
                        productID={'guid://Zakeke/' + uuid() + '/' + toLocalID(model.id)}
                        forEditProduct={false}
                        forAddingNewSalesChannel={true}
                        merchantProductOptionsConnected={[]}
                        additionalCodes={model.additionalCodes.map((c) => {
                            return { code: c.code, salesChannelID: c.salesChannelID, merchantProductName: c.merchantProductName ?? '' };
                        })}
                        productColors={model.colors as ProductColor[]}
                        salesChannels={availableSalesChannels}
                        productSalesChannelID={model.salesChannel.id}
                        onConfirm={async (sourceProduct, salesChannelId) => {
                            if (!model) {
                                showDialog('no-selection', <MessageDialog message={T._('Select a product', 'Admin')} />);
                                return;
                            }

                            if (!sourceProduct) return;

                            closeDialog('connectProductDialog');

                            const loadingNotification = showNotification({
                                type: 'default',
                                message: T._('Publishing your customizable product...', 'Admin')
                            });

                            let response!: productListConnectModelMutation$data;

                            try {
                                // Save product
                                response = await new Promise((resolve, reject) =>
                                    commitConnectModel({
                                        onCompleted: (response) => resolve(response),
                                        onError: (response) => reject(response),
                                        updater: (store) => {
                                            store.invalidateStore();
                                        },
                                        variables: {
                                            id: model.id,
                                            target: {
                                                name: sourceProduct!.name,
                                                sku: sourceProduct!.sku,
                                                imageUrl: sourceProduct!.thumbnail,
                                                salesChannelId
                                            }
                                        }
                                    })
                                );

                                showNotification({
                                    type: 'success',
                                    message: T._('Product connected and published!', 'Admin'),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });
                            } catch (ex: any) {
                                showNotification({
                                    type: 'danger',
                                    message: (
                                        <>
                                            {T._('Failed connecting and publishing product.', 'Admin')}
                                            <br />
                                            {ex.errors?.[0].extensions?.message}
                                        </>
                                    ),
                                    dismiss: {
                                        duration: 3000
                                    }
                                });
                            }
                            removeNotification(loadingNotification);
                        }}
                    />
                </Suspense>
            );
        }
    };

    const handleDuplicate = (model: PreviewModel) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        showDialog(
            'duplicate',
            <DuplicateDialog
                sourceProductCode={model!.code}
                salesChannelID={model!.salesChannel.id}
                onConfirm={async (targets) => {
                    closeDialog('duplicate');

                    const loadingNotification = showNotification({
                        type: 'default',
                        message: T._('The product is duplicating...', 'Admin')
                    });

                    let response!: testPageDuplicateMutation$data;

                    try {
                        // Save product
                        response = await new Promise<testPageDuplicateMutation$data>((resolve, reject) =>
                            commitDuplicate({
                                onCompleted: (response) => resolve(response),
                                onError: (response) => reject(response),
                                updater: (store) => {
                                    store.invalidateStore();
                                },
                                variables: {
                                    id: model!.id,
                                    targets: targets.map((x) => ({
                                        name: x.name!,
                                        sku: x.sku!,
                                        imageUrl: x.thumbnail,
                                        salesChannelId: model?.salesChannel.id!
                                    }))
                                }
                            })
                        );
                        if (response.duplicateModel.status === 'WARNING') {
                            showWarningNotification({
                                message: T._('There is already a duplicating product request in progress. Please wait for it to complete.', 'Admin')
                            });
                        } else {
                            showNotification({
                                type: 'success',
                                message: T._('Products duplicated!', 'Admin'),
                                dismiss: {
                                    duration: 3000
                                }
                            });
                            window.location.href = backofficeUrl('/products');
                        }
                    } catch (ex: any) {
                        showNotification({
                            type: 'danger',
                            message: (
                                <>
                                    {T._('Failed duplicating products.', 'Admin')}
                                    <br />
                                    {ex.errors?.[0].extensions?.message}
                                </>
                            ),
                            dismiss: {
                                duration: 3000
                            }
                        });
                    }
                    removeNotification(loadingNotification);
                }}
            />
        );
    };
    const handleDeleteClick = (model: PreviewModel) => {
        if (!isAllowed(['PRODUCTS_DELETE'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        showDialog(
            'delete-product',
            <QuestionDialog
                message={stringFormat(T._('Do you want delete the product "{0}"?', 'Admin'), model!.name)}
                onYesClick={() => {
                    closeDialog('delete-product');
                    showInfoNotification({
                        message: T._('Please wait. The product is deleting..', 'Admin'),
                        dismiss: {
                            duration: 5000
                        }
                    });
                    deleteProduct({
                        variables: {
                            productID: model!.id
                        },
                        onError: () => {
                            showErrorNotification({ message: T._('Failed deleting product', 'Admin') });
                        },
                        onCompleted: () => {
                            showSuccessNotification({ message: T._('Product deleted.', 'Admin') });
                            incrementStoreKey();
                            navigate(backofficeUrl('/products'));
                        },
                        updater: (store) => store.invalidateStore()
                    });
                }}
            />
        );
    };

    const handleMarketplacePreviewClick = (productID: string) => {
        if (!isAllowed(['PRODUCTS_EDIT'])) {
            showErrorNotification({ message: T._('You do not have permission to perform this action.', 'Admin') });
            return;
        }

        showDialog('marketplace-preview-url', <MarketplacePreviewUrlDialog productID={productID} />);
    };

    const composerIframeRef = useRef<HTMLIFrameElement>(null);
    const customizerIframeRef = useRef<HTMLIFrameElement>(null);
    const discoveryReadyRef = useRef(false);
    // Whether the customizer is fully loaded AND (if prompted) a variant has been chosen — gates the
    // discovery tool buttons. Driven by the customizer's DiscoveryModeInteractive beacon; reset to false
    // on every iframe remount (product switch / mode toggle / reload) so the buttons re-disable while the
    // fresh customizer boots.
    const [discoveryInteractive, setDiscoveryInteractive] = useState(false);
    // Ids of AI/Tools discovery pills the customizer reports as already active on the opened product; the
    // panel hides them. Driven by the DiscoveryModeHiddenTools beacon; reset to [] on every iframe remount.
    const [discoveryHiddenTools, setDiscoveryHiddenTools] = useState<string[]>([]);
    const isComposer = model.zakekeEnvironment === 'COMPOSER';
    const getBaseIframeUrl = useCallback(
        (testUrl: string) => {
            let url = testUrl;
            // Configurator (COMPOSER) products must not receive viewerMode in the iframe URL — strip it
            // from the backend-provided test URL.
            if (isComposer) {
                url = removeIframeSearchParam(url, 'viewerMode');
            }
            // The customizer's "Place test order" (add-to-cart) stays always visible. When the seller
            // has no test orders left we intercept the add-to-cart message and prompt them to buy more.
            if (isPrototypeMode) {
                url = setIframeSearchParam(url, 'isPrototype', '1');
            }
            return url;
        },
        [isComposer, isPrototypeMode]
    );
    const baseIframeUrl = useMemo(() => getBaseIframeUrl(model.iframeUrl.testUrl), [getBaseIframeUrl, model.iframeUrl.testUrl]);

    useEffect(() => {
        if (discoveryMode) return undefined;

        const onTestModeMessage = (event: MessageEvent) => {
            const iframe = isComposer ? composerIframeRef.current : customizerIframeRef.current;
            if (!iframe || event.source !== iframe.contentWindow || !isTestModeReadyMessage(event.data)) return;

            postAddonsList(iframe, addons, addonsCurrency);
        };

        window.addEventListener('message', onTestModeMessage);
        return () => window.removeEventListener('message', onTestModeMessage);
    }, [addons, addonsCurrency, discoveryMode, isComposer]);

    // Composer products: send postMessage to load the product in the iframe
    useEffect(() => {
        if (!isComposer) return;

        const sendLoadMessage = () => {
            composerIframeRef.current?.contentWindow?.postMessage(
                {
                    type: 'load',
                    parameters: {
                        name: model.name,
                        token: config.token,
                        ecommerce: 'api',
                        integrationVersion: 2,
                        currency: 'USD',
                        culture: config.culture || 'en',
                        modelCode: model.code,
                        qty: 1,
                        attributes: []
                    }
                },
                '*'
            );
        };

        const interval = setInterval(sendLoadMessage, 500);

        const handleMessage = (event: MessageEvent) => {
            if (event.data?.zakekeType === 'loaded') {
                clearInterval(interval);
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            clearInterval(interval);
            window.removeEventListener('message', handleMessage);
        };
    }, [isComposer, model.name, model.code]);

    // Configurator (COMPOSER) products don't support Discovery Mode: force Test Mode only, even if the
    // URL arrived with ?mode=discovery. The Test/Discovery switch is also hidden for these products.
    useEffect(() => {
        if (isComposer && discoveryMode) {
            setDiscoveryMode(false);
        }
    }, [isComposer, discoveryMode]);

    // Entering discovery mode reloads the iframe with discoverymode=1: the URL change flows into
    // the iframe `key` below, remounting it = a clean restart that boots the customizer in
    // discovery mode. Per-category selections are NOT applied through the URL anymore — once
    // booted, the customizer listens for DiscoveryModeUpdate postMessages (no reload per click).
    const iframeSrc = useMemo(() => {
        const base = baseIframeUrl;
        if (!base) return base;
        if (!discoveryMode) return base;
        return setIframeSearchParam(base, 'discoverymode', '1');
    }, [baseIframeUrl, discoveryMode]);

    // Discovery mode handshake: the customizer announces readiness (DiscoveryModeReady) once it has
    // booted in discovery mode — this just establishes the channel. It later beacons DiscoveryModeInteractive
    // once it is truly ready for input (fully loaded and, if prompted, a variant chosen); that boolean drives
    // whether the DiscoveryModePanel tool buttons are enabled.
    useEffect(() => {
        if (!discoveryMode) {
            discoveryReadyRef.current = false;
            setDiscoveryInteractive(false);
            setDiscoveryHiddenTools([]);
            return undefined;
        }
        const onDiscoveryMessage = (event: MessageEvent) => {
            if (!customizerIframeRef.current || event.source !== customizerIframeRef.current.contentWindow) return;

            if (isDiscoveryModeReadyMessage(event.data)) {
                discoveryReadyRef.current = true;
                postAddonsList(customizerIframeRef.current, addons, addonsCurrency);
            } else if (isDiscoveryModeInteractiveMessage(event.data)) {
                setDiscoveryInteractive(event.data.interactive);
            } else if (isDiscoveryModeHiddenToolsMessage(event.data)) {
                setDiscoveryHiddenTools(event.data.hiddenTools);
            } else if (isDiscoveryAiGenerationCompletedMessage(event.data)) {
                // A real AI generation just consumed a credit. Re-resolve and, if the shared pool is now
                // spent, open the buy-credits dialog so the seller can top up (they can keep the tool open).
                void resolveAiCredits().then((state) => {
                    setAiCreditCase(state.creditCase);
                    if (state.creditCase === 'out-of-credits') openPurchaseDialog();
                });
            }
        };
        window.addEventListener('message', onDiscoveryMessage);
        return () => window.removeEventListener('message', onDiscoveryMessage);
    }, [addons, addonsCurrency, discoveryMode, resolveAiCredits, openPurchaseDialog]);

    // Any change to the iframe `key` below (product switch, discovery toggle, resize, manual reload)
    // remounts the iframe = a fresh customizer boot, so the previous "interactive" state is stale. Re-disable
    // the tool buttons until the newly booted customizer beacons DiscoveryModeInteractive again.
    useEffect(() => {
        setDiscoveryInteractive(false);
        setDiscoveryHiddenTools([]);
    }, [iframeSrc, effectivePreviewSize, iframeReloadNonce]);
    useAutoAddonActivation(customizerIframeRef, !isComposer);

    return (
        <FullscreenIframeOverlay>
            <FullscreenIframeTopBar $discovery={discoveryMode}>
                <FullscreenIframeTopBarLeft>
                    {!isPrototypeMode && (
                        <ZTooltip position='bottom' title={T._('Switch product', 'Admin')} enterDelay={0} exitDelay={0} interactive={false}>
                            <ChangeProductButton
                                $discovery={discoveryMode}
                                onClick={() => {
                                    showDialog(
                                        'product-catalog',
                                        <ProductCatalogDialog
                                            onConfirm={(modelID) => {
                                                closeDialog('product-catalog');
                                                const newProductId = String(modelID);
                                                startTransition(() => {
                                                    setActiveProductId(newProductId);
                                                });
                                                navigate(backofficeUrl(`/test-mode/${newProductId}`), { replace: true });
                                            }}
                                        />
                                    );
                                }}
                            >
                                <Icon>
                                    <ChangeProductIcon />
                                </Icon>
                            </ChangeProductButton>
                        </ZTooltip>
                    )}
                    <FullscreenIframeTitle $discovery={discoveryMode}>{model.name}</FullscreenIframeTitle>
                    {!isPrototypeMode && !discoveryMode ? (
                        <>
                            <BadgeContainer>
                                <StyledBadge
                                    color={model.statusID === 1 ? '#065f46' : '#374151'}
                                    badgeColor={model.statusID === 1 ? '#d6f3e2' : '#f3f4f6'}
                                    showCircle={false}
                                >
                                    {model.statusID === 1 ? T._('Online', 'Admin') : T._('Not published', 'Admin')}
                                </StyledBadge>
                            </BadgeContainer>
                            <PublishSwitchWrapper>
                                <ZSwitch checked={isPublished} onChange={() => handleSetStatusClick(model)} disabled={!isAllowed(['PRODUCTS_PUBLISH'])} />
                                <PublishSwitchLabel>{isPublished ? T._('Published', 'Admin') : T._('Unpublished', 'Admin')}</PublishSwitchLabel>
                            </PublishSwitchWrapper>
                            {isPublished && model.salesChannel.ecommerceSettings?.definitiveUrl && (
                                <Link href={model.salesChannel.ecommerceSettings.definitiveUrl} target='_blank'>
                                    <Icon>
                                        <ExternalLinkIcon />
                                    </Icon>
                                </Link>
                            )}
                        </>
                    ) : null}
                </FullscreenIframeTopBarLeft>
                <FullscreenIframeTopBarCenter>
                    {!isPrototypeMode && isDiscoveryModeEnabled && !isComposer && (
                        <ModeToggleContainer>
                            <ModeToggleButton
                                $active={!discoveryMode}
                                $variant='test'
                                onClick={() => {
                                    setDiscoveryMode(false);
                                    setDiscoverySelections({ printing: null, pricing: null, ai: null, tools: null });
                                }}
                            >
                                {T._('Test Mode', 'Admin')}
                            </ModeToggleButton>
                            <ModeToggleButton $active={discoveryMode} $variant='discovery' onClick={() => setDiscoveryMode(true)}>
                                {T._('Discovery Mode', 'Admin')}
                            </ModeToggleButton>
                        </ModeToggleContainer>
                    )}
                </FullscreenIframeTopBarCenter>
                <FullscreenIframeTopBarRight>
                    {!isComposer &&
                        subscription?.maxNumTestOrders != null &&
                        subscription?.inTrial === false &&
                        shopifyAuthEcommerceSettings?.isTestStore !== true && (
                            <ZTooltip position='bottom' title={T._('The test order limit resets every month', 'Admin')}>
                                <TestOrderNumberInBillingDateer $warning={subscription.isOverMaxNumTestOrders}>
                                    {subscription.isOverMaxNumTestOrders ? (
                                        T._('No Test Orders left. Buy more.', 'Admin')
                                    ) : (
                                        <>
                                            {T._('Test orders:', 'Admin')} {Math.max(subscription.maxNumTestOrders - testOrderNumberInBillingDate, 0)}
                                        </>
                                    )}
                                </TestOrderNumberInBillingDateer>
                            </ZTooltip>
                        )}
                    {!isPrototypeMode && !isOldUserInterface && !discoveryMode && (
                        <ZTooltip
                            position='bottom'
                            title={
                                previewSize === 'desktop-wide'
                                    ? T._('Desktop', 'Admin')
                                    : previewSize === 'desktop'
                                      ? T._('Laptop', 'Admin')
                                      : T._('Mobile', 'Admin')
                            }
                        >
                            <RoundIconButton
                                type='button'
                                aria-label={T._('Preview layout', 'Admin')}
                                onClick={() =>
                                    setPreviewSize((prev) => {
                                        const order: LivePreviewSize[] = ['desktop-wide', 'desktop', 'mobile'];
                                        return order[(order.indexOf(prev) + 1) % order.length];
                                    })
                                }
                            >
                                <Icon>
                                    {previewSize === 'desktop-wide' ? <DesktopWideIcon /> : previewSize === 'desktop' ? <DesktopIcon /> : <MobileIcon />}
                                </Icon>
                            </RoundIconButton>
                        </ZTooltip>
                    )}
                    {!isPrototypeMode && !discoveryMode && !isComposer && (
                        <ZTooltip position='bottom' title={T._('Product details', 'Admin')}>
                            <RoundIconButton type='button' aria-label={T._('Product details', 'Admin')} onClick={() => setShowSidebar(true)}>
                                <Icon>
                                    <ProductDetailsIcon />
                                </Icon>
                            </RoundIconButton>
                        </ZTooltip>
                    )}
                    {!isPrototypeMode && !discoveryMode && (
                        <DropdownActions
                            header={
                                <RoundIconButton type='button' aria-label={T._('Actions', 'Admin')}>
                                    <Icon>
                                        <VerticalThreePoint />
                                    </Icon>
                                </RoundIconButton>
                            }
                            items={[
                                !isComposer && <DropdownActionItem onClick={() => setShowSidebar(true)}>{T._('Product details', 'Admin')}</DropdownActionItem>,
                                <DropdownActionItem
                                    onClick={() => {
                                        if (isComposer) {
                                            window.location.href = `/Composer?id=${toLocalID(model!.id)}&page=product`;
                                        } else {
                                            navigate(backofficeUrl('/Product?product_id=' + toLocalID(model!.id)));
                                        }
                                    }}
                                >
                                    {T._('Edit Product', 'Admin')}
                                </DropdownActionItem>,
                                model.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' &&
                                    model.provider !== 'INVITION' &&
                                    model.provider !== 'CUSTOM_CAT' &&
                                    model.provider !== 'PRINTFUL' && (
                                        <DropdownActionItem onClick={() => handleDuplicate(model)}>{T._('Duplicate', 'Admin')}</DropdownActionItem>
                                    ),
                                ...(isComposer
                                    ? []
                                    : [
                                          model.salesChannel.ecommerceSettings?.ecommerceName !== 'NONE' && model.canAddAdditionalCode && (
                                              <DropdownActionItem onClick={() => handleAddSalesChannels(model.id, model.salesChannel.id)}>
                                                  {T._('Add to a Sales Channel', 'Admin')}
                                              </DropdownActionItem>
                                          ),
                                          (model.salesChannel.ecommerceSettings?.ecommerceName === 'ETSY' ||
                                              model.additionalCodes.some((c) => c.salesChannel.ecommerceSettings?.ecommerceName === 'ETSY')) && (
                                              <DropdownActionItem onClick={() => handleMarketplacePreviewClick(model!.id)} disabled={model.statusID !== 1}>
                                                  {T._('Generate a shareable demo product URL', 'Admin')}
                                              </DropdownActionItem>
                                          )
                                      ]),
                                <DropdownDivider />,
                                <DropdownActionItem onClick={() => handleDeleteClick(model)} danger>
                                    {T._('Delete', 'Admin')}
                                </DropdownActionItem>
                            ]}
                            orientation={'RIGHT'}
                        />
                    )}
                    {!isPrototypeMode && discoveryMode && (
                        <UISettingsButton onClick={() => navigate(backofficeUrl('/settings/UserInterface'))}>
                            {T._('User Interface Settings', 'Admin')}
                        </UISettingsButton>
                    )}
                    <FullscreenIframeCloseButton onClick={() => navigate(backofficeUrl('/products'))} aria-label={T._('Close', 'Admin')}>
                        <Icon>
                            <CloseIcon />
                        </Icon>
                    </FullscreenIframeCloseButton>
                </FullscreenIframeTopBarRight>
            </FullscreenIframeTopBar>

            {(isClassic || publishedInThisSession) &&
                !isPrototypeMode &&
                !discoveryMode &&
                !isComposer &&
                isShopifyDuplicationWarningVisible &&
                model.salesChannel.ecommerceSettings?.ecommerceName === 'SHOPIFY' && (
                    <BannerSection>
                        <NotificationBanner
                            type='WARNING'
                            title={getShopifyDuplicationWarningTitle()}
                            showCloseButton={true}
                            onClose={() => {
                                localStorage.setItem('testPageShopifyDuplicationWarningDismissed', 'true');
                                setIsShopifyDuplicationWarningVisible(false);
                            }}
                            content={getShopifyDuplicationWarningText()}
                            buttons={[
                                {
                                    label: T._('Learn more', 'Admin'),
                                    linkButton: true,
                                    href: SHOPIFY_DUPLICATION_WARNING_LEARN_MORE_URL
                                },
                                {
                                    label: T._('Change behavior', 'Admin'),
                                    onClick: () => navigate(backofficeUrl(SHOPIFY_INTEGRATION_SETTINGS_PATH))
                                }
                            ]}
                        />
                    </BannerSection>
                )}

            {!isPrototypeMode && !discoveryMode && !isComposer && isInfoBannerVisible && (
                <BannerSection>
                    <NotificationBanner
                        type='INFO'
                        showCloseButton={true}
                        onClose={() => {
                            localStorage.setItem('testPageInfoBannerDismissed', 'true');
                            setIsInfoBannerVisible(false);
                        }}
                        content={
                            <span>
                                {T._('This preview shows what your customers will see. Test the customization flow and improve it by configuring a', 'Admin')}{' '}
                                <Link onClick={() => navigate(backofficeUrl('/printing-methods'))}>{T._('Printing Method', 'Admin')}</Link>
                                {T._(', using advanced', 'Admin')} <Link onClick={() => navigate(backofficeUrl('/tools'))}>{T._('Tools', 'Admin')}</Link>
                                {T._(', or leveraging', 'Admin')}{' '}
                                <Link onClick={() => navigate(backofficeUrl('/ai-creative-tools'))}>{T._('AI Creative Tools', 'Admin')}</Link>{' '}
                                {T._('to enhance the personalization experience.', 'Admin')}
                            </span>
                        }
                    />
                </BannerSection>
            )}

            {!isPrototypeMode && discoveryMode && (
                <Suspense fallback={null}>
                    <DiscoveryCollagePrefetch onLoaded={(payload) => (collagePayloadRef.current = payload)} />
                </Suspense>
            )}

            {!isPrototypeMode && discoveryMode && (
                <DiscoveryModePanel
                    customizerReady={discoveryInteractive}
                    hiddenTools={discoveryHiddenTools}
                    selections={discoverySelections}
                    onSelect={handleDiscoverySelect}
                    aiBanner={
                        discoverySelections.ai !== null && aiCreditCase === 'needs-activation' ? (
                            <DiscoveryAiActivationBanner onActivate={handleActivateCredits} isActivating={isActivatingCredits} />
                        ) : null
                    }
                    quantity={discoveryQuantity}
                    quantityPresets={DISCOVERY_QUANTITY_PRESETS}
                    onQuantityChange={handleDiscoveryQuantityChange}
                    onSave={(category, item) => {
                        // Printing: an "informed" redirect to the print-method create page. Carry the
                        // streamed print-type payload in router state (the create form seeds every field
                        // from it via discoveryPrintTypeToSeed) and pre-assign the sandbox product through
                        // ?fromProductId (the numeric model id the form expects).
                        if (category === 'printing') {
                            const productLocalId = toLocalID(activeProductId);
                            const target = productLocalId ? `/printing-method?fromProductId=${productLocalId}` : '/printing-method';
                            navigate(backofficeUrl(target), {
                                state: { discoveryPrintType: DISCOVERY_PRINTING_PAYLOADS[item.id] }
                            });
                            return;
                        }
                        // Pricing: same "informed redirect" as printing. Carry the streamed pricing-rule DSL in
                        // router state (the create form rebuilds the canonical rule from it via
                        // discoveryPricingRuleToSeed) and pre-assign the sandbox product through ?fromProductId.
                        if (category === 'pricing') {
                            const productLocalId = toLocalID(activeProductId);
                            const target = productLocalId ? `/pricing-rule?fromProductId=${productLocalId}` : '/pricing-rule';
                            navigate(backofficeUrl(target), {
                                state: { discoveryPricingRule: DISCOVERY_PRICING_PAYLOADS[item.id], discoveryPricingName: item.name }
                            });
                            return;
                        }
                        // Tools: route each activated tool to its real create/setup page and pre-assign
                        // the sandbox product through ?fromProductId — the same convention printing/pricing
                        // use, which each tool's create form already reads to seed its product association
                        // (see quoteRule/gridRule associatedProducts initializers). Previously the whole
                        // category fell through to `/tools?from=discovery&tool=…`; nothing consumed those
                        // params so it rendered the generic Tools menu (blank on a discovery account).
                        // Unlike printing/pricing there is no self-contained rule payload to carry here: a
                        // tool "rule" references catalog entities (shapes, grids) that only ever existed in
                        // the streamed customizer sandbox, so we seed the product only.
                        if (category === 'tools') {
                            const productLocalId = toLocalID(activeProductId);
                            const withProduct = productLocalId ? `?fromProductId=${productLocalId}` : '';
                            const toolRoutes: Record<string, string> = {
                                'get-a-quote': `/tools/get-a-quote/quote-rule${withProduct}`,
                                'collage-layouts': `/tools/collage-layouts/rule${withProduct}`,
                                // Shapes auto-assigns ?fromProductId only in embedded (chrome-less) mode, so
                                // open the full-page Shape Gallery for manual assignment rather than degrade
                                // the layout — no clean full-page product pre-assignment exists for it.
                                'shapes-and-masks': '/tools/shapes/my-gallery'
                            };
                            navigate(backofficeUrl(toolRoutes[item.id] ?? '/tools'));
                            return;
                        }
                        const routes: Record<string, string> = {
                            ai: `/ai?from=discovery&tool=${item.id}`
                        };
                        navigate(backofficeUrl(routes[category] ?? '/'));
                    }}
                    onBuildYourOwn={(category) => {
                        const routes: Record<string, string> = {
                            printing: '/printing-method',
                            pricing: '/pricings',
                            ai: '/ai-creative-tools',
                            tools: '/tools'
                        };
                        navigate(backofficeUrl(routes[category] ?? '/'));
                    }}
                />
            )}

            <FullscreenIframeContent>
                <PreviewFrameContainer ref={previewContainerRef}>
                    <WaitForProductExists productId={model.id} fallback={<TestPageSkeleton />}>
                        {effectivePreviewSize === 'desktop-wide' ? (
                            <FullscreenIframe
                                ref={isComposer ? composerIframeRef : customizerIframeRef}
                                key={`${iframeSrc}-${effectivePreviewSize}-${iframeReloadNonce}`}
                                src={iframeSrc}
                                allow='camera; microphone; autoplay; fullscreen; picture-in-picture'
                                allowFullScreen
                            />
                        ) : (
                            <PreviewSkin $width={skinConfig.width} $height={skinConfig.height} $scale={skinConfig.scale} $skin={skinConfig.skin}>
                                <FullscreenIframe
                                    ref={isComposer ? composerIframeRef : customizerIframeRef}
                                    key={`${iframeSrc}-${effectivePreviewSize}-${iframeReloadNonce}`}
                                    src={iframeSrc}
                                    allow='camera; microphone; autoplay; fullscreen; picture-in-picture'
                                    allowFullScreen
                                />
                            </PreviewSkin>
                        )}
                    </WaitForProductExists>
                </PreviewFrameContainer>
            </FullscreenIframeContent>

            {showSidebar && (
                <ProductSidePanel
                    productId={model.id}
                    onClose={() => setShowSidebar(false)}
                    onProductChange={refreshProductIframe}
                    onPreview={() => {
                        setShowSidebar(false);
                    }}
                    onPublishToggle={() => {
                        handleSetStatusClick(model);
                    }}
                    onDelete={() => {
                        setShowSidebar(false);
                        handleDeleteClick(model);
                    }}
                />
            )}
        </FullscreenIframeOverlay>
    );
};

export default TestPage;
